/**
 * ContentEditable Editing Plugin
 *
 * Handles inline editing for simple text nodes (headers, sticky notes)
 * using native contentEditable. Supports:
 * - Focus + cursor placement
 * - Escape to cancel, Enter to save
 * - Blur to save
 * - Event isolation from canvas input system
 * - Optional live font-size fitting callback
 */

import type {
	EditingPlugin,
	EditingPluginContext,
	EditingSession,
	EditingResult,
} from '../editing';

export interface ContentEditablePluginOptions {
	/**
	 * Optional callback for live input events (e.g., font-size fitting for sticky notes).
	 * Called on every keystroke while editing.
	 */
	onInput?: (
		element: HTMLElement,
		nodeId: string,
		ctx: EditingPluginContext,
	) => void;
}

const EDITING_CARET_SENTINEL = '\u200B';

function isPlaceholderElement(element: HTMLElement): boolean {
	return (
		element.classList.contains('header-empty') ||
		element.classList.contains('sticky-note-empty')
	);
}

function normalizeEditableText(text: string): string {
	return text.replace(/\u200B/g, '');
}

/**
 * Create a contentEditable editing plugin.
 *
 * @param nodeTypes - Node types this plugin handles (e.g., ['header', 'sticky-note'])
 * @param editableSelector - CSS selector for the editable element
 * @param options - Optional configuration
 */
export function createContentEditablePlugin(
	nodeTypes: string[],
	editableSelector: string,
	options?: ContentEditablePluginOptions,
): EditingPlugin {
	return {
		nodeTypes,
		editableSelector,
		startEditing(ctx: EditingPluginContext): EditingSession {
			const { element, input } = ctx;
			const originalContent = ctx.originalContent;

			// Mark as editing before any render
			element.classList.add('editing');

			// Push editing mode on input system
			input.pushMode('editing');

			// Clear placeholder content
			const isPlaceholder = isPlaceholderElement(element);
			if (isPlaceholder) {
				element.classList.remove('header-empty', 'sticky-note-empty');
				element.innerText = EDITING_CARET_SENTINEL;
			}

			// Make editable and focus
			element.contentEditable = 'true';
			element.focus();

			// Place cursor at end
			const range = document.createRange();
			const selection = window.getSelection();
			range.selectNodeContents(element);
			range.collapse(false);
			selection?.removeAllRanges();
			selection?.addRange(range);

			// Event handlers
			let stopped = false;
			let saveOnStop = true;

			const blurHandler = () => {
				if (!stopped) ctx.requestStop(true);
			};

			const keydownHandler = (e: KeyboardEvent) => {
				e.stopPropagation();
				if (e.key === 'Escape') {
					e.preventDefault();
					ctx.requestStop(false);
				} else if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					ctx.requestStop(true);
				}
			};

			const inputHandler = options?.onInput
				? () => options.onInput!(element, ctx.nodeId, ctx)
				: null;

			// Register handlers
			element.addEventListener('blur', blurHandler);
			element.addEventListener('keydown', keydownHandler);
			if (inputHandler) {
				element.addEventListener('input', inputHandler);
				// Apply immediately so initial state is correct
				element.style.overflowY = 'hidden';
			}

			function stopSession(save: boolean): EditingResult {
				if (stopped) {
					return {
						content: save
							? normalizeEditableText(element.innerText || '')
							: originalContent,
					};
				}
				stopped = true;
				saveOnStop = save;

				// Remove handlers
				element.removeEventListener('blur', blurHandler);
				element.removeEventListener('keydown', keydownHandler);
				if (inputHandler) {
					element.removeEventListener('input', inputHandler);
				}
				element.style.overflowY = '';

				// Get content
				const newContent = normalizeEditableText(element.innerText || '');

				// Reset element state
				element.contentEditable = 'false';
				element.classList.remove('editing');

				// Pop editing mode
				input.popMode('editing');

				return {
					content: save ? newContent : originalContent,
				};
			}

			return {
				getContent: () => normalizeEditableText(element.innerText || ''),
				stop: (save: boolean) => stopSession(save),
			};
		},
	};
}
